import { useOrganization } from '@clerk/react';
import type { AutocompleteInputChangeReason, AutocompleteRenderOptionState } from '@mui/material';
import { Box, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, useTheme } from '@mui/material';
import { RiAttachment2, RiCloseLine, RiDownloadLine, RiEyeLine } from '@remixicon/react';
import saveAs from 'file-saver';
import { Form, FormikProvider, useFormik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isArray, isNil } from 'lodash';
import Mustache from 'mustache';
import { type HTMLAttributes, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'src/auth';
import Autocomplete from 'src/components/atoms/Autocomplete';
import LoadingButton from 'src/components/atoms/buttons/LoadingButton';
import FormField from 'src/components/atoms/form/FormField';
import UnderlinedMatch from 'src/components/atoms/UnderlinedMatch';
import type {
  EmailContentDocument,
  EmailDocument,
  EmailRecipient,
} from 'src/components/containers/modals/emailDocument.types';
import { AgButton, AgText } from 'src/components/ui';
import { notify } from 'src/lib/notify';
import { requirePresent } from 'src/lib/requirePresent';
import {
  DocumentViewer,
  filestackPdfConversionUrl,
  isSpreadsheetMimeType,
  isSpreadsheetName,
  printUrl,
  ZoomControlsMinified,
} from 'src/modules/entities/documents';
import formatFileSize from 'src/utils/formatFileSize';
import fuzzyComplete from 'src/utils/fuzzyComplete';
import getFullName from 'src/utils/getFullName';
import { z } from 'zod';
import { useRecipients } from '../hooks/useRecipients';
import { useSendEmail } from '../hooks/useSendEmail';
import type { EmailTemplateProps } from '../types';

type EmailDocumentGeneration = EmailContentDocument & {
  pdf: { url: string | null; blob: Blob | null; title?: string; loading: boolean } | null;
  handleDocumentUpload?: () => Promise<number>;
};

export type SendEmailModalProps = {
  documents?: EmailDocument[];
  defaultToRecipient?: EmailRecipient | null;
  defaultCCRecipients?: EmailRecipient[];
  handleClose: () => void;
  open: boolean;
  afterDialog?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  defaultSubject?: string;
  handleSendSuccess?: () => void;
  emailTemplateProps?: EmailTemplateProps;
  businessEntityId?: number;
  hideBackdrop?: boolean;

  /**
   *
   */
  documentGeneration?: EmailDocumentGeneration;
};

const formZod = z.object({
  subject: z.string().nonempty('Add a subject.'),
  toRecipient: z.object(
    {
      id: z.number(),
      knockId: z.string({ message: "This recipient can't receive emails yet. Pick another recipient." }),
      name: z.string(),
      email: z
        .string({ message: 'This recipient has no valid email address.' })
        .email('This recipient has no valid email address.'),
    },
    { message: 'Choose who to send to.' }
  ),
  ccRecipients: z.array(z.unknown()),
  body: z.string().nonempty('Add a message.'),
});

type SendEmailFormErrors = Partial<Record<keyof z.infer<typeof formZod>, string>>;

/** One message per field, keyed by the field it belongs to so Formik marks that field. */
const sendEmailFormErrors = (values: unknown): SendEmailFormErrors | undefined => {
  const result = formZod.safeParse(values);
  if (result.success) return undefined;

  const errors: SendEmailFormErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0] as keyof SendEmailFormErrors;
    errors[field] ??= issue.message;
  }
  return errors;
};

/** The To address also listed in CC is sent once, as To; the API drops it from CC the same way. */
const ccWithoutToRecipient = <R extends { email?: string | null }>(
  ccRecipients: readonly (R | null | undefined)[],
  toEmail: string | null | undefined
): R[] => ccRecipients.filter((r): r is R => Boolean(r) && r?.email !== toEmail);

type EmailTemplateContent = { subject: string; body: string };

type BaseEmailTemplates = { OTHER: EmailTemplateContent } & Record<string, EmailTemplateContent | undefined>;

const baseEmailTemplates: BaseEmailTemplates = {
  BILL_OF_LADING: { subject: 'Bill of Lading', body: `Please see the attached Bill(s) of Lading for your reference.` },
  CARRIER_CONFIRMATION: {
    subject: 'Carrier Confirmation',
    body: `Please see the attached Carrier Confirmation for your reference.`,
  },
  GROWER_SETTLEMENT: {
    subject: 'Grower Settlement',
    body: `Please see the attached Grower Settlement for your reference.`,
  },
  INVOICE: { subject: 'Invoice', body: `Please see the attached Invoice for your reference.` },
  LOT_SETTLEMENT: { subject: 'Lot Settlement', body: `Please see the attached Lot Settlement for your reference.` },
  MULTI_BILL_OF_LADING: {
    subject: 'Bill of Lading',
    body: `Please see the attached Bill(s) of Lading for your reference.`,
  },
  OTHER: { subject: 'Other', body: `Please see the attached document for your reference.` },
  PASSING: { subject: 'Passing', body: `Please see the attached Passing for your reference.` },
  PAYABLE: { subject: 'Payable', body: `Please see the attached Payable for your reference.` },
  PICK_TICKET: { subject: 'Pick Ticket', body: `Please see the attached Pick Ticket for your reference.` },
  PRODUCTION_RUN_REPORT: {
    subject: 'Production Run Report',
    body: `Please see the attached Production Run Report for your reference.`,
  },
  PURCHASE_ORDER_CONFIRMATION: {
    subject: 'Purchase Order Confirmation',
    body: `Please see the attached Purchase Order Confirmation for your reference.`,
  },
  RECEIVING_TICKET: {
    subject: 'Receiving Ticket',
    body: `Please see the attached Receiving Ticket for your reference.`,
  },
  SALES_ORDER_CONFIRMATION: {
    subject: 'Sales Order Confirmation',
    body: `Please see the attached Sales Order Confirmation for your reference.`,
  },
  SHIPMENT_CONFIRMATION: {
    subject: 'Shipment Confirmation',
    body: `Please see the attached Shipment Confirmation for your reference.`,
  },
  UNKNOWN: { subject: 'Unknown', body: `Please see the attached document for your reference.` },
  WORK_ORDER_CONFIRMATION: {
    subject: 'Work Order Confirmation',
    body: `Please see the attached Work Order Confirmation for your reference.`,
  },
  REMITTANCE: { subject: 'Remittance', body: `Please see the attached Remittance for your reference.` },

  // TODO: .. Add any new document types here
};

type EmailTemplate = {
  default: EmailTemplateContent;
  types: { [key: string]: { subject: string; body: string } | undefined };
  custom: { [key: string]: { subject: string; body: string } };
};

const getPopulatedEmailContent = (
  documents: EmailContentDocument[] | undefined,
  toRecipients: EmailRecipient | null | undefined,
  documentData: unknown,
  emailTemplates: EmailTemplate
) => {
  if (!documents?.length) {
    return { subject: '', body: '' };
  }

  const typeCounts = documents
    .filter((doc) => doc.type !== 'CUSTOM')
    .reduce<Record<string, number>>((acc, doc) => {
      const documentType = String(doc.type);
      acc[documentType] = (acc[documentType] || 0) + 1;
      return acc;
    }, {});

  const customCounts = documents
    .filter((doc) => doc.type === 'CUSTOM')
    .reduce<Record<string, number>>((acc, doc) => {
      const documentTemplateId = String(doc.documentTemplateId);
      acc[documentTemplateId] = (acc[documentTemplateId] || 0) + 1;
      return acc;
    }, {});

  const customLabels = documents
    .filter((doc) => doc.type === 'CUSTOM')
    .reduce<Record<string, string>>((acc, doc) => {
      if (doc.documentTemplate?.name) {
        acc[String(doc.documentTemplateId)] = doc.documentTemplate.name;
      }
      return acc;
    }, {});

  // Generate subject and body
  const subjectParts = [];
  const bodyParts = [];

  if (!emailTemplates) {
    return;
  }

  for (const [type, count] of Object.entries(typeCounts)) {
    const template = emailTemplates.types?.[type] || emailTemplates.default;

    if (!template) {
      const baseTemplate = baseEmailTemplates[type] || baseEmailTemplates['OTHER'];

      subjectParts.push(`${baseTemplate.subject}${count > 1 ? ` (${count})` : ''}`);
      bodyParts.push(baseTemplate.body);

      continue;
    }

    const data = Object.assign({}, documentData, {
      recipients: toRecipients,
      label: type === 'CUSTOM' ? customLabels[type] : baseEmailTemplates[type]?.subject,
    });

    subjectParts.push(`${Mustache.render(template.subject ?? '', data)}${count > 1 ? ` (${count})` : ''}`);
    bodyParts.push(Mustache.render(template.body ?? '', data));
  }

  for (const [id, count] of Object.entries(customCounts)) {
    const template = emailTemplates.custom?.[id] || emailTemplates.default;

    if (!template) {
      const baseTemplate = baseEmailTemplates['OTHER'];

      subjectParts.push(`${baseTemplate.subject}${count > 1 ? ` (${count})` : ''}`);
      bodyParts.push(baseTemplate.body);

      continue;
    }

    subjectParts.push(`${Mustache.render(template.subject ?? '', documentData)}${count > 1 ? ` (${count})` : ''}`);
    bodyParts.push(Mustache.render(template.body ?? '', documentData));
  }

  const subject = subjectParts.join('; ');
  const body = bodyParts.join('\n\n');

  return { subject, body };
};

const getDefaultEmailContent = (documents: EmailContentDocument[] | undefined) => {
  if (!documents?.length) {
    return { subject: '', body: '' };
  }

  // Count the number of each document type
  const docTypeCounts = documents.reduce<Record<string, number>>((acc, doc) => {
    const documentType = String(doc.type);
    acc[documentType] = (acc[documentType] || 0) + 1;
    return acc;
  }, {});

  // Generate subject and body
  const subjectParts = [];
  const bodyParts = [];

  for (const [type, count] of Object.entries(docTypeCounts)) {
    const template = baseEmailTemplates[type] || baseEmailTemplates['OTHER'];

    subjectParts.push(`${template.subject}${count > 1 ? ` (${count})` : ''}`);
    bodyParts.push(template.body);
  }

  const subject = subjectParts.join('; ');
  const body = bodyParts.join('\n\n');

  return { subject, body };
};

type EmailDropDownOption = {
  id: number;
  knockId?: string | null;
  name: string;
  email?: string | null;
  businessEntity?: { name: string };
};

type SelectableEmailDropDownOption = EmailDropDownOption & { email: string };

const isSelectableEmailDropDownOption = (option: EmailDropDownOption): option is SelectableEmailDropDownOption =>
  Boolean(option.email);

export default function SendEmailModal({
  documents,
  defaultToRecipient,
  defaultCCRecipients,
  defaultSubject = '',
  handleClose,
  open,
  afterDialog,
  maxWidth,
  handleSendSuccess,
  emailTemplateProps,
  businessEntityId,
  documentGeneration,
  hideBackdrop,
}: SendEmailModalProps) {
  const { send: sendEmail } = useSendEmail();
  const {
    contacts: contactRecipients,
    memberships,
    search: searchRecipients,
  } = useRecipients({ businessEntityId, open });

  const { organization } = useOrganization();
  const { currentUser } = useAuth();
  const currentUserEmails = useMemo(
    (): Array<string | null | undefined> =>
      Array.isArray(currentUser?.emailAddress) ? currentUser.emailAddress : [currentUser?.emailAddress],
    [currentUser]
  );

  const [initialValuesDirty, setInitialValuesDirty] = useState(false);

  const flags = useFlags();
  const emailTemplates = flags['emailTemplates'];

  const initialValues = useMemo(() => {
    let subject, body;

    const docs = documentGeneration ? [documentGeneration] : documents;

    if (emailTemplates && emailTemplateProps?.templateData) {
      const populatedEmailContent = getPopulatedEmailContent(
        docs,
        defaultToRecipient,
        emailTemplateProps?.templateData,
        emailTemplates[emailTemplateProps?.type]
      );

      if (populatedEmailContent) {
        subject = populatedEmailContent.subject;
        body = populatedEmailContent.body;
      }
    }

    const defaultContent = getDefaultEmailContent(docs);

    if (!subject) {
      subject = defaultContent.subject;
    }

    if (!body) {
      body = defaultContent.body;
    }

    setInitialValuesDirty(true);

    const addCurrentUser =
      currentUser &&
      defaultToRecipient?.email !== currentUser.emailAddress &&
      !defaultCCRecipients?.some((r) => currentUserEmails.includes(r.email));

    let ccRecipients = defaultCCRecipients ?? [];

    if (addCurrentUser) {
      ccRecipients = [
        ...ccRecipients,
        {
          id: currentUser.userId,
          knockId: currentUser.knockId,
          name: getFullName(currentUser),
          email: currentUserEmails[0],
        },
      ];
    }

    return {
      subject: defaultSubject || subject,
      toRecipient: !isNil(defaultToRecipient) ? defaultToRecipient : null,
      ccRecipients,
      body: body,
    };
  }, [
    defaultSubject,
    defaultToRecipient,
    defaultCCRecipients,
    documents,
    currentUser,
    currentUserEmails,
    emailTemplates,
    emailTemplateProps?.templateData,
    emailTemplateProps?.type,
    documentGeneration,
  ]);

  const formik = useFormik({
    initialValues,
    onSubmit: async ({ toRecipient, ccRecipients, ...form }) => {
      let documentIds = documents?.map((doc) => doc.id);

      if (documentGeneration?.handleDocumentUpload) {
        const generatedDocumentId = await documentGeneration.handleDocumentUpload();
        documentIds = [generatedDocumentId];
      }

      const recipient = requirePresent(toRecipient, 'id');

      sendEmail({
        input: {
          documentIds,
          toRecipient: { id: recipient.id, knockId: recipient.knockId, name: recipient.name, email: recipient.email },
          ccRecipients: ccWithoutToRecipient(ccRecipients, recipient.email).map((user) => ({
            id: user.id,
            knockId: user.knockId,
            name: user.name,
            email: user.email,
          })),
          subject: form.subject,
          body: form.body,
        },
      })
        .then(({ data }) => {
          if (data?.sendEmail) {
            notify.success('Documents sent successfully');
          } else {
            notify.error('Documents failed to send');
          }
          handleSendSuccess?.();
        })
        .catch(() => {
          handleClose();
        });

      // don't close the modal if we're waiting for the send success result
      if (!handleSendSuccess) {
        handleClose();
      }
      formik.resetForm();
    },
    validateOnChange: false,
    validateOnBlur: false,
    // Runs only on submit (validateOnChange/Blur are off), so a blocked send always says why.
    validate: (values) => {
      const errors = sendEmailFormErrors(values);
      const [firstMessage] = Object.values(errors ?? {});
      if (firstMessage) notify.error(firstMessage);
      return errors;
    },
  });

  // Errors are only computed on Send, so drop them once the user edits a field rather than leave a stale one.
  const { setErrors } = formik;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the field values are the trigger, not inputs
  useEffect(() => {
    setErrors({});
  }, [formik.values.subject, formik.values.toRecipient, formik.values.body, setErrors]);

  useEffect(() => {
    if (!open && initialValuesDirty) {
      formik.resetForm({ values: initialValues });
    }

    setInitialValuesDirty(false);
  }, [open, formik, initialValues, initialValuesDirty]);

  const contacts = useMemo<EmailDropDownOption[]>(() => {
    return [
      ...memberships.map(({ user }) => {
        const membershipUser = requirePresent(user, 'id');

        return {
          id: membershipUser.id,
          knockId: membershipUser.knockId,
          name: getFullName(membershipUser),
          email: membershipUser.email,
          businessEntity: { name: organization?.name ?? '' },
        };
      }),
      ...contactRecipients,
    ];
  }, [memberships, contactRecipients, organization]);

  /** Typing searches the server; MUI syncing the box to a picked value resets to the first page. */
  const onRecipientInputChange = (_event: unknown, value: string, reason: AutocompleteInputChangeReason) =>
    searchRecipients(reason === 'input' ? value : '');

  const filteredOptionsForTo = useMemo(() => {
    return contacts
      .filter((contact) => contact.email !== formik.values.toRecipient?.email)
      .filter(isSelectableEmailDropDownOption);
  }, [contacts, formik.values.toRecipient]);

  const filteredOptionsForCC = useMemo(() => {
    if (!isArray(formik.values.ccRecipients)) {
      return [];
    }

    const toEmails = new Set(formik.values.ccRecipients.map((r) => r?.email).filter(Boolean));
    return contacts
      .filter(isSelectableEmailDropDownOption)
      .filter((contact) => !toEmails.has(contact.email) && !currentUserEmails.includes(contact.email));
  }, [contacts, formik.values.ccRecipients, currentUserEmails]);

  const renderEmailOption = (option: SelectableEmailDropDownOption, inputText: string) => (
    <div>
      <UnderlinedMatch target={option.name} input={inputText} />
      <AgText size="14">
        <UnderlinedMatch target={option.email} input={inputText} />
      </AgText>
    </div>
  );

  const [previewDocument, setPreviewDocument] = useState<{
    url: string | null;
    blob: Blob | null;
    title?: string;
  } | null>(null);

  useEffect(() => {
    if (documentGeneration?.pdf) {
      setPreviewDocument(documentGeneration.pdf);
    }
  }, [documentGeneration?.pdf]);

  const closeModal = () => {
    handleClose();

    // don't close the preview document if it's generated.
    // this way, we keep the preview open if the modal closes
    if (!documentGeneration) {
      setPreviewDocument(null);
    }
  };

  const theme = useTheme();

  const fetchDocument = async (doc: EmailDocument) => {
    const conversionUrl = filestackPdfConversionUrl(doc.url);
    const looksLikeSpreadsheet = isSpreadsheetName(doc.title) || isSpreadsheetName(doc.url);

    // Spreadsheets: fetch the Filestack PDF-conversion URL so they feed the same
    // PDF preview pipeline. Detect cheaply from the extension up front, and fall
    // back to mime-type detection on the fetched blob when the extension is unknown.
    if (looksLikeSpreadsheet && conversionUrl) {
      const response = await fetch(conversionUrl);
      const blob = await response.blob();
      return { blob, url: URL.createObjectURL(blob), title: doc.title };
    }

    const response = await fetch(doc.url);
    let blob = await response.blob();

    if (isSpreadsheetMimeType(blob.type) && conversionUrl) {
      const converted = await fetch(conversionUrl);
      blob = await converted.blob();
    }

    return { blob, url: URL.createObjectURL(blob), title: doc.title };
  };

  return (
    <>
      <Dialog open={open} onClose={closeModal} fullWidth maxWidth={maxWidth || 'md'} hideBackdrop={hideBackdrop}>
        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            <FormikProvider value={formik}>
              <Form>
                <DialogTitle>Send Documents</DialogTitle>
                <DialogContent sx={{ minHeight: '480px', pr: 0 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ pr: 3, display: 'flex', flexDirection: 'column' }}>
                        <FormField
                          // eslint-disable-next-line jsx-a11y/no-autofocus
                          autoFocus
                          margin="dense"
                          name="subject"
                          fieldProps={{ type: 'text', label: 'Subject', helperText: formik.errors.subject }}
                          fullWidth
                          continuousSync={false}
                        />

                        <FormField
                          margin="dense"
                          name="toRecipient"
                          Field={Autocomplete<SelectableEmailDropDownOption, true>}
                          fieldProps={{
                            options: filteredOptionsForTo,
                            label: 'To',
                            textFieldProps: {
                              error: Boolean(formik.errors.toRecipient),
                              helperText: formik.errors.toRecipient,
                            },
                            isOptionEqualToValue: (
                              option: SelectableEmailDropDownOption,
                              value: SelectableEmailDropDownOption
                            ) => option.knockId === value.knockId,
                            getOptionKey: (option: SelectableEmailDropDownOption) => option.email,
                            getOptionLabel: (option: SelectableEmailDropDownOption) => option.name,
                            filterOptions: fuzzyComplete(['email', 'name']),
                            onInputChange: onRecipientInputChange,
                            renderOption: (
                              _: HTMLAttributes<HTMLLIElement>,
                              option: SelectableEmailDropDownOption,
                              state: AutocompleteRenderOptionState
                            ) => renderEmailOption(option, state.inputValue),
                            // groupBy: (option) => option.businessEntity?.name?.trim() ?? '',
                          }}
                          useValue={true}
                          continuousSync={false}
                        />

                        <FormField
                          margin="dense"
                          name="ccRecipients"
                          Field={Autocomplete<SelectableEmailDropDownOption, true>}
                          fieldProps={{
                            options: filteredOptionsForCC,
                            label: 'cc:',
                            isOptionEqualToValue: (
                              option: SelectableEmailDropDownOption,
                              value: SelectableEmailDropDownOption
                            ) => option.knockId === value.knockId,
                            getOptionKey: (option: SelectableEmailDropDownOption) => option.email,
                            getOptionLabel: (option: SelectableEmailDropDownOption) => option.name,
                            filterOptions: fuzzyComplete(['email', 'name']),
                            onInputChange: onRecipientInputChange,
                            renderOption: (
                              _: HTMLAttributes<HTMLLIElement>,
                              option: SelectableEmailDropDownOption,
                              state: AutocompleteRenderOptionState
                            ) => renderEmailOption(option, state.inputValue),
                            details: (option: SelectableEmailDropDownOption) => (
                              <AgText size="14">{option.email}</AgText>
                            ),
                            multiple: true,
                          }}
                          useValue={true}
                          continuousSync={false}
                        />

                        <FormField
                          margin="dense"
                          name="body"
                          fieldProps={{
                            type: 'text',
                            label: 'Message',
                            multiline: true,
                            rows: 4,
                            helperText: formik.errors.body,
                          }}
                          continuousSync={false}
                        />
                      </Box>
                      {documents && (
                        <>
                          <Divider sx={{ mt: 1, mr: 3 }} />
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                              maxHeight: '100px',
                              pr: 3,
                              mt: 1,
                              overflowY: 'auto',
                            }}
                          >
                            {documents.map((doc) => {
                              return (
                                <Box
                                  key={doc.id}
                                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                >
                                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <RiAttachment2 size={20} style={{ color: theme.palette.primary[500] }} />
                                    <AgText size="16" color="secondary">
                                      {doc.title}
                                    </AgText>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <AgButton
                                      variant="ghost"
                                      onPress={async () => {
                                        const previewDocument = await fetchDocument(doc);

                                        if (previewDocument.blob.type !== 'application/pdf') {
                                          notify.error('Preview only available for PDF and spreadsheet documents');
                                          return;
                                        }

                                        setPreviewDocument(previewDocument);
                                      }}
                                    >
                                      <RiEyeLine size={18} />
                                      Open In Preview
                                    </AgButton>
                                    <LoadingButton
                                      handleRequest={async () => {
                                        try {
                                          const downloadedDoc = await fetchDocument(doc);

                                          saveAs(downloadedDoc.blob, `${doc.title}.pdf`);
                                        } catch (error) {
                                          console.error('Download failed', error);
                                        }
                                      }}
                                    >
                                      <RiDownloadLine size={20} className="mr-1" />
                                      Download
                                    </LoadingButton>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                          <Divider sx={{ mt: 1, mr: 3 }} />
                        </>
                      )}
                    </Box>
                    {afterDialog}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <AgButton variant="ghost" onPress={closeModal}>
                    Cancel
                  </AgButton>
                  <AgButton variant="primary" type="submit">
                    Send
                  </AgButton>
                </DialogActions>
              </Form>
            </FormikProvider>
          </Box>
          <Collapse in={previewDocument !== null} orientation="horizontal" collapsedSize="0px">
            <Box sx={{ height: '100%', display: 'flex' }}>
              <Divider orientation="vertical" />
              <Box>
                <Box sx={{ mx: 2, mt: 2, mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        width: '40px',
                        height: '40px',
                        borderRadius: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.primary[100],
                      }}
                    >
                      <RiAttachment2 size={18} />
                    </Box>
                    <Box>
                      <AgText size="14" color="secondary">
                        {previewDocument?.title}
                      </AgText>
                      <AgText size="14" className="text-[#767773]">
                        {formatFileSize(
                          previewDocument === null ? undefined : requirePresent(previewDocument.blob, 'size').size
                        )}
                      </AgText>
                    </Box>
                  </Box>
                  <Box>
                    {!documentGeneration && (
                      <AgButton
                        variant="ghost"
                        size="icon-sm"
                        shape="circle"
                        onPress={() => {
                          setPreviewDocument(null);
                        }}
                      >
                        <RiCloseLine size={18} />
                      </AgButton>
                    )}
                  </Box>
                </Box>
                <>
                  <DocumentViewer
                    height="446px"
                    width="460px"
                    pdf={previewDocument}
                    handleDownload={(print = false) => {
                      const document = requirePresent(previewDocument, 'url');

                      if (print) {
                        printUrl(requirePresent(document.url, 'url'));
                      } else {
                        saveAs(requirePresent(document.blob, 'blob'), `${document.title}.pdf`);
                      }
                    }}
                    controlsStyling={{ px: 2 }}
                    Controls={ZoomControlsMinified}
                  />
                </>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Dialog>
    </>
  );
}
